import React from 'react';
import { useTranslation } from 'react-i18next';
import { Transition } from '@headlessui/react';

import { printOdd, getCurrencySymbol } from 'shared/utils/common';
import { formatCurrency } from 'shared/money/formatCurrency';
import {
  calculateDutchingPreview,
  calculateSystemPreview,
  calculateTotalOdds,
  getTicketSlipResolvedType,
  hasDuplicateEvents,
  isSystemRelevant,
} from 'shared/utils/ticket-slip-helper';
import { useTicketSlipStore } from 'features/tickets/store';
import { isGuestUserSession, UserSessionContext } from 'features/session/userSessionStore';
import { useCreditAvailableQuery } from 'features/tickets/queries';
import ModernTips from 'features/tickets/components/ModernTips';
import ModernConfirmBetButton from 'features/tickets/components/ModernConfirmBetButton';
import MyBets from 'features/tickets/components/MyBets';
import DynamicSpinner from 'shared/utils/loader';
import { cn } from 'lib/utils';

/**
 * Overlays and panels are positioned relative to the app root (#c7-virtual-app-root)
 * so they stay within the visible virtual-web slot in both standalone and embedded
 * layouts. The app root owns the height contract, so overlays can use absolute
 * positioning without leaking into the host shell.
 */

function formatRange(minimum: number, maximum: number, symbol: string): string {
  const min = Number(minimum || 0).toFixed(2);
  const max = Number(maximum || 0).toFixed(2);
  if (minimum === maximum) {
    return `${max}${symbol}`;
  }
  return `${min}${symbol} - ${max}${symbol}`;
}

function useBetslipSummary() {
  const { t } = useTranslation();
  const ticket_slip = useTicketSlipStore((s) => s.ticket_slip);
  const total_stake = useTicketSlipStore((s) => s.total_stake);
  const bet_mode = useTicketSlipStore((s) => s.bet_mode);
  const stake_mode = useTicketSlipStore((s) => s.stake_mode);
  const system_rows = useTicketSlipStore((s) => s.system_rows);
  const [{ user }] = React.useContext(UserSessionContext);

  if (ticket_slip.length === 0) {
    return {
      t,
      hasSelections: false,
      countLabel: '0',
    };
  }

  const symbol = getCurrencySymbol(user?.currency);
  const systemPreview = calculateSystemPreview(ticket_slip, system_rows);
  const dutchingPreview = calculateDutchingPreview(ticket_slip);
  const resolvedType = getTicketSlipResolvedType(ticket_slip, bet_mode, system_rows, stake_mode);
  const totalOddsCalculated = calculateTotalOdds(ticket_slip);
  const standardPotentialWin = (total_stake * totalOddsCalculated).toFixed(2);
  const displayStake = resolvedType === 'SYSTEM'
    ? systemPreview.totalStake
    : resolvedType === 'DUTCHING'
      ? dutchingPreview.totalStake
      : total_stake;
  const payout = resolvedType === 'SYSTEM'
    ? formatRange(systemPreview.minimumPotentialPayout, systemPreview.maximumPotentialPayout, symbol)
    : resolvedType === 'DUTCHING'
      ? formatRange(dutchingPreview.minimumPotentialPayout, dutchingPreview.maximumPotentialPayout, symbol)
      : `${standardPotentialWin}${symbol}`;

  return {
    t,
    hasSelections: true,
    countLabel: ticket_slip.length > 9 ? '9+' : String(ticket_slip.length),
    stake: `${displayStake.toFixed(2)}${symbol}`,
    payout,
  };
}

function BetslipSlipContent() {
  const { t, i18n } = useTranslation();
  const ticket_slip = useTicketSlipStore((s) => s.ticket_slip);
  const total_stake = useTicketSlipStore((s) => s.total_stake);
  const bet_mode = useTicketSlipStore((s) => s.bet_mode);
  const stake_mode = useTicketSlipStore((s) => s.stake_mode);
  const system_rows = useTicketSlipStore((s) => s.system_rows);
  const loading = useTicketSlipStore((s) => s.loading);
  const setTotalStake = useTicketSlipStore((s) => s.setTotalStake);
  const setSystemRowStake = useTicketSlipStore((s) => s.setSystemRowStake);
  const removeAllTips = useTicketSlipStore((s) => s.removeAllTips);
  const [{ user }] = React.useContext(UserSessionContext);
  const isGuest = isGuestUserSession(user);
  const { data: creditData } = useCreditAvailableQuery({ enabled: !isGuest });
  const balance = creditData?.credit_available != null
    ? Number(creditData.credit_available)
    : Number(user?.player_balance ?? 0);
  const currency = (user?.currency as string) ?? '';
  const symbol = getCurrencySymbol(user?.currency);
  const systemRelevant = !isGuest && isSystemRelevant(ticket_slip);
  const systemPreview = calculateSystemPreview(ticket_slip, system_rows);
  const dutchingPreview = calculateDutchingPreview(ticket_slip);
  const resolvedType = getTicketSlipResolvedType(ticket_slip, bet_mode, system_rows, stake_mode);
  const is_single = ticket_slip.length > 0 && resolvedType === 'SINGLE';
  const is_multiple = ticket_slip.length > 0 && resolvedType === 'MULTIPLE';
  const is_dutching = ticket_slip.length > 0 && resolvedType === 'DUTCHING';
  const is_system = ticket_slip.length > 0 && resolvedType === 'SYSTEM';
  const odds_format = user?.odds_format || 'decimal';
  const total_odds_calculated = calculateTotalOdds(ticket_slip);
  const total_odds = printOdd(total_odds_calculated, odds_format);
  const standardPotentialWin = (total_stake * total_odds_calculated).toFixed(2);
  const hasSystemConflict = is_system && !systemPreview.valid;
  const hasMultipleConflict = is_multiple && hasDuplicateEvents(ticket_slip);
  const hasActiveSystemRows = system_rows.some((row) => Number(row.stake_per_line) > 0);
  const hasInvalidSystemStakeStep = system_rows.some((row) => {
    const stakePerLine = Number(row.stake_per_line);
    return stakePerLine > 0 && stakePerLine % 0.5 !== 0;
  });
  const hasInvalidDutchingStakeStep = ticket_slip.some((tip) => {
    const stake = Number(tip.stake);
    return stake > 0 && stake % 0.5 !== 0;
  });
  const displayStake = is_system
    ? systemPreview.totalStake
    : is_dutching
      ? dutchingPreview.totalStake
      : total_stake;

  const stakeValid = is_system
    ? systemPreview.valid &&
      hasActiveSystemRows &&
      !hasInvalidSystemStakeStep &&
      Number.isFinite(systemPreview.totalStake) &&
      systemPreview.totalStake > 0 &&
      systemPreview.totalStake <= balance
    : is_dutching
      ? dutchingPreview.valid &&
        !hasInvalidDutchingStakeStep &&
        dutchingPreview.totalStake > 0 &&
        dutchingPreview.totalStake <= balance
    : Number.isFinite(total_stake) &&
      !hasMultipleConflict &&
      total_stake > 0 &&
      total_stake % 0.5 === 0 &&
      total_stake <= balance;
  const balanceAfterBet = stakeValid ? balance - displayStake : null;

  const onStakeChange = (
    e:
      | React.ChangeEvent<HTMLInputElement>
      | React.KeyboardEvent<HTMLInputElement>
      | React.ClipboardEvent<HTMLInputElement>,
  ) => {
    const target = e.currentTarget;
    if (target && 'value' in target) setTotalStake(Number((target as HTMLInputElement).value));
  };

  const onSystemStakeChange = (size: number) => (
    e:
      | React.ChangeEvent<HTMLInputElement>
      | React.KeyboardEvent<HTMLInputElement>
      | React.ClipboardEvent<HTMLInputElement>
  ) => {
    const target = e.currentTarget;
    if (target && 'value' in target) {
      setSystemRowStake(size, Number((target as HTMLInputElement).value));
    }
  };

  if (!ticket_slip.length) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center py-16 px-6 text-center bg-card">
        <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center mb-5">
          <i className="bi bi-receipt text-3xl text-muted-foreground" />
        </div>
        <p className="text-base font-semibold text-foreground mb-1">{t('CLICK_ON_ODD_TO_INSERT')}</p>
        <p className="text-sm text-muted-foreground">{t('BETSLIP')}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-1 min-h-0 flex-col">
      <DynamicSpinner status={loading} />

      <div className="px-5 py-3 border-b border-border bg-muted/30 flex items-center justify-between gap-3">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
            {t('BET_TYPE', 'Bet type')}
          </div>
          <div className="text-sm font-bold text-foreground">
            {t(resolvedType, resolvedType)}
          </div>
        </div>
        {systemRelevant && !is_system && (
          <div className="text-right text-[11px] text-muted-foreground max-w-[190px]">
            {t(
              'SYSTEM_ROWS_HINT',
              'Add a stake to a system row below to turn this slip into a SYSTEM bet.'
            )}
          </div>
        )}
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto bg-background">
        <ModernTips />
        {/* Footer: stake + summary + actions */}
        <div className="border-t-2 border-neutral/20 bg-card">
          {hasMultipleConflict && (
            <div className="px-5 pt-4">
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                {t(
                  'MULTIPLE_INVALID_DUPLICATE_EVENTS',
                  'MULTIPLE bets do not allow multiple selections from the same event. Edit individual leg stakes to switch to DUTCHING.'
                )}
              </div>
            </div>
          )}
          {systemRelevant && (
            <div className="px-5 pt-4 space-y-3">
              {hasSystemConflict && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                  {t(
                    'SYSTEM_INVALID_ZERO_EFFECTIVE_COMBINATIONS',
                    'At least one active SYSTEM row has no valid combinations. Adjust the rows or selections before confirming.'
                  )}
                </div>
              )}
              <div className="rounded-lg border border-border bg-muted/40 p-3 space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="font-semibold text-foreground">{t('SYSTEM_ROWS', 'System rows')}</span>
                  <span className="text-xs text-muted-foreground">
                    {systemPreview.totalCombinations} {t('COMBINATIONS', 'combinations')}
                  </span>
                </div>
                <div className="space-y-2">
                  {system_rows.map((row) => {
                    const previewRow = systemPreview.rows.find((item) => item.size === row.size);
                    return (
                      <div
                        key={row.size}
                        className="grid grid-cols-[1fr_auto] gap-3 rounded-md border border-border/80 bg-background px-3 py-2"
                      >
                        <div className="min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-sm font-semibold text-foreground">
                              {t('SYSTEM_ROW_LABEL', { size: row.size, total: ticket_slip.length, defaultValue: `${row.size}/${ticket_slip.length}` })}
                            </span>
                            <span className="text-[11px] text-muted-foreground">
                              {previewRow?.combinationCount ?? 0} {t('LINES', 'lines')}
                            </span>
                          </div>
                          <div className="text-[11px] text-muted-foreground">
                            {t('SYSTEM_ROW_PAYOUT_RANGE', {
                              range: formatRange(
                                previewRow?.minimumPotentialPayout ?? 0,
                                previewRow?.maximumPotentialPayout ?? 0,
                                symbol
                              ),
                              defaultValue: `Range ${formatRange(
                                previewRow?.minimumPotentialPayout ?? 0,
                                previewRow?.maximumPotentialPayout ?? 0,
                                symbol
                              )}`,
                            })}
                          </div>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs font-semibold text-muted-foreground">{symbol}</span>
                          <input
                            className="w-20 px-2 py-1.5 text-sm font-semibold border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-neutral text-right bg-background text-foreground"
                            disabled={loading}
                            readOnly={loading}
                            value={Number(row.stake_per_line || 0)}
                            onChange={onSystemStakeChange(row.size)}
                            onKeyUp={onSystemStakeChange(row.size)}
                            onPaste={onSystemStakeChange(row.size)}
                            aria-label={t('STAKE_PER_LINE', 'Stake per line')}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
          {!isGuest && (
            <div className="px-5 pt-3 flex items-center justify-between text-sm">
              <span className="text-muted-foreground">{t('BALANCE', 'Balance')}</span>
              <span className="font-semibold text-foreground">{formatCurrency(balance, currency, i18n?.language as string)}</span>
            </div>
          )}
          {is_system ? (
            <div className="px-5 py-3 flex items-center justify-between">
              <span className="text-sm font-semibold text-foreground">{t('TOTAL_STAKE')}</span>
              <span className="text-sm font-bold text-foreground">
                {formatCurrency(systemPreview.totalStake, currency, i18n?.language as string)}
              </span>
            </div>
          ) : is_dutching ? (
            <div className="px-5 py-3 flex items-center justify-between">
              <div>
                <div className="text-sm font-semibold text-foreground">{t('TOTAL_STAKE')}</div>
                <div className="text-[11px] text-muted-foreground">
                  {t('DUTCHING_STAKE_HINT', 'The total stake is the sum of the individual leg stakes.')}
                </div>
              </div>
              <span className="text-sm font-bold text-foreground">
                {formatCurrency(dutchingPreview.totalStake, currency, i18n?.language as string)}
              </span>
            </div>
          ) : (
            <div className="px-5 py-3 flex items-center justify-between">
              <label htmlFor="betslip-total-stake" className="text-sm font-semibold text-foreground">
                {t('TOTAL_STAKE')}
              </label>
              <div className="flex items-center gap-1.5">
                <span className="text-sm font-semibold text-muted-foreground">
                  {symbol}
                </span>
                <input
                  id="betslip-total-stake"
                  className="w-24 px-3 py-2 text-sm font-semibold border-2 border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral focus:border-neutral text-right bg-background text-foreground transition-colors"
                  disabled={loading}
                  readOnly={loading}
                  value={total_stake || 0.0}
                  onChange={onStakeChange}
                  onKeyUp={onStakeChange}
                  onPaste={onStakeChange}
                  aria-label={t('TOTAL_STAKE')}
                />
              </div>
            </div>
          )}

          {balanceAfterBet != null && (
            <div className="px-5 pb-2 flex items-center justify-between text-sm">
              <span className="text-muted-foreground">{t('BALANCE_AFTER_BET', 'Balance after bet')}</span>
              <span className="font-semibold text-foreground">{formatCurrency(balanceAfterBet, currency, i18n?.language as string)}</span>
            </div>
          )}

          <div className="mx-5 mb-3 p-3 rounded-lg bg-muted/60 border border-border space-y-1.5">
            {(is_single || is_multiple) && (
              <div className="flex justify-between items-center text-sm">
                <span className="text-muted-foreground">{t('TOTAL_ODD')}</span>
                <span className="font-bold text-foreground">{total_odds}</span>
              </div>
            )}
            {is_system ? (
              <>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-muted-foreground">{t('SYSTEM_ACTIVE_ROWS', 'Active rows')}</span>
                  <span className="font-bold text-foreground">
                    {systemPreview.rows.length}
                  </span>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-muted-foreground">{t('POSSIBLE_PAYOUT')}</span>
                  <span className="font-bold text-primary">
                    {formatRange(systemPreview.minimumPotentialPayout, systemPreview.maximumPotentialPayout, symbol)}
                  </span>
                </div>
              </>
            ) : is_dutching ? (
              <>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-muted-foreground">{t('TOTAL_BETS', 'Bets')}</span>
                  <span className="font-bold text-foreground">{ticket_slip.length}</span>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-muted-foreground">{t('POSSIBLE_PAYOUT')}</span>
                  <span className="font-bold text-primary">
                    {formatRange(dutchingPreview.minimumPotentialPayout, dutchingPreview.maximumPotentialPayout, symbol)}
                  </span>
                </div>
              </>
            ) : (
              <div className="flex justify-between items-center text-sm">
                <span className="text-muted-foreground">{t('POSSIBLE_PAYOUT')}</span>
                <span className="font-bold text-primary">
                  {standardPotentialWin} {symbol}
                </span>
              </div>
            )}
          </div>

          <div className="px-5 pb-5 pt-1 space-y-3">
            <ModernConfirmBetButton />
            <button
              onClick={() => removeAllTips()}
              className="w-full flex items-center justify-center gap-2 py-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded-lg transition-colors disabled:opacity-40"
              disabled={loading}
              aria-label={t('DELETE_ALL_BETS')}
            >
              <i className="bi bi-trash3 text-sm" />
              {t('DELETE_ALL_BETS')}
            </button>
          </div>
        </div>
      </div>
    </div>
    
  );
}

function BetslipContent({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const ticket_slip = useTicketSlipStore((s) => s.ticket_slip);
  const activeTab = useTicketSlipStore((s) => s.betslipTab);
  const setBetslipTab = useTicketSlipStore((s) => s.setBetslipTab);

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* Single header row: Betslip | My Bets + close */}
      <div className="bg-neutral-800 text-neutral-100 rounded-t-[inherit] px-4 py-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-1">
          <button
            type="button"
            className={cn(
              'px-3 py-1.5 rounded-md text-xs font-semibold uppercase tracking-wider transition-colors',
              activeTab === 'slip'
                ? 'bg-white/25 text-neutral-100'
                : 'bg-transparent text-white/60 hover:bg-white/10 hover:text-white/80'
            )}
            onClick={() => setBetslipTab('slip')}
          >
            {t('BETSLIP')}
          </button>
          {activeTab === 'slip' && ticket_slip.length > 0 && (
            <span className="bg-white/25 text-neutral-100 text-xs font-bold rounded-full min-w-[22px] h-[22px] flex items-center justify-center px-1.5">
              {ticket_slip.length}
            </span>
          )}
          <span className="text-white/40 mx-0.5" aria-hidden="true">|</span>
          <button
            type="button"
            className={cn(
              'px-3 py-1.5 rounded-md text-xs font-semibold uppercase tracking-wider transition-colors',
              activeTab === 'mybets'
                ? 'bg-white/25 text-neutral-100'
                : 'bg-transparent text-white/60 hover:bg-white/10 hover:text-white/80'
            )}
            onClick={() => setBetslipTab('mybets')}
          >
            {t('MY_BETS', 'My Bets')}
          </button>
        </div>
        <button
          onClick={onClose}
          className="w-8 h-8 flex items-center justify-center rounded-full bg-white/15 hover:bg-white/25 transition-colors flex-shrink-0"
          aria-label={t('CLOSE', 'Close')}
        >
          <i className="bi bi-x-lg text-sm" />
        </button>
      </div>

      {activeTab === 'slip' ? (
        <BetslipSlipContent />
      ) : (
        <div className="flex-1 min-h-0 overflow-y-auto bg-background p-3">
          <MyBets />
        </div>
      )}
    </div>
  );
}

function MobilePanel({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const positionClass = 'absolute inset-0';
  return (
    <>
      <Transition show={isOpen} as={React.Fragment}>
        <Transition.Child
          as="div"
          className={cn(positionClass, 'bg-black/40 z-[99]')}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
          onClick={onClose}
          aria-hidden="true"
        />
      </Transition>

      <Transition show={isOpen} as={React.Fragment}>
        <div className={cn(positionClass, 'overflow-y-auto pointer-events-none z-[120]')}>
          <Transition.Child
            as="div"
            className="flex min-h-full items-end justify-center px-2 pb-2 pt-6 pointer-events-auto"
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-full"
            enterTo="opacity-100 translate-y-0"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-full"
          >
            <div
              className="w-full max-w-md max-h-full overflow-hidden rounded-2xl bg-card text-left shadow-2xl flex flex-col"
              role="dialog"
              aria-label="Betslip"
              onClick={(e) => e.stopPropagation()}
            >
              <BetslipContent onClose={onClose} />
            </div>
          </Transition.Child>
        </div>
      </Transition>
    </>
  );
}

function DesktopPanel({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  return (
    <>
      {isOpen && (
        <div
          className="absolute inset-0 bg-black/40 z-[99] transition-opacity duration-300"
          onClick={onClose}
        />
      )}

      <div
        className={cn(
          'absolute inset-y-0 right-0 w-full max-w-[420px] min-h-0 bg-card border-l-2 border-neutral/25 shadow-2xl z-[110] transition-transform duration-300 ease-out flex flex-col',
          isOpen ? 'translate-x-0' : 'translate-x-full pointer-events-none',
        )}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Betslip"
      >
        {isOpen && <BetslipContent onClose={onClose} />}
      </div>
    </>
  );
}

function DesktopTrigger({
  isOpen,
  onOpen,
}: {
  isOpen: boolean;
  onOpen: () => void;
}) {
  const summary = useBetslipSummary();

  if (isOpen) return null;

  const buttonClass = cn(
    'absolute bottom-4 right-4 z-[80]',
    'bg-neutral text-neutral-foreground shadow-xl hover:shadow-2xl hover:bg-neutral/90',
    'flex flex-col items-center gap-0.5 px-4 py-2.5 rounded-xl',
    'transition-all duration-200 pointer-events-auto',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
  );

  if (!summary.hasSelections) {
    return (
      <button type="button" onClick={onOpen} className={buttonClass} aria-label={summary.t('BETSLIP')}>
        <span className="flex items-center gap-2 text-sm font-bold uppercase tracking-wide">
          <i className="bi bi-receipt text-base" />
          {summary.t('BETSLIP')}
        </span>
        <span className="text-[11px] font-medium text-white/80 text-center max-w-[220px] leading-snug">
          {summary.t('BETSLIP_FAB_EMPTY_HINT')}
        </span>
      </button>
    );
  }

  return (
    <button type="button" onClick={onOpen} className={buttonClass} aria-label={summary.t('BETSLIP')}>
      <span className="text-xs font-medium text-white/90 whitespace-nowrap">
        {summary.t('TOTAL_STAKE')}: <span className="font-bold text-neutral-foreground">{summary.stake}</span>
        {' · '}
        {summary.t('POSSIBLE_PAYOUT')}: <span className="font-bold text-primary">{summary.payout}</span>
      </span>
      <span className="flex items-center gap-2 text-sm font-bold uppercase tracking-wide">
        <i className="bi bi-receipt text-base" />
        {summary.t('BETSLIP')}
        <span className="bg-white/30 text-neutral-foreground text-xs font-bold rounded-full min-w-[22px] h-[22px] flex items-center justify-center">
          {summary.countLabel}
        </span>
      </span>
    </button>
  );
}

function MobileSummaryBar({
  isOpen,
  onOpen,
}: {
  isOpen: boolean;
  onOpen: () => void;
}) {
  const summary = useBetslipSummary();

  if (isOpen || !summary.hasSelections) return null;

  return (
    <div className="absolute bottom-[4.75rem] left-0 right-0 z-[80] px-2">
      <button
        type="button"
        onClick={onOpen}
        className="w-full rounded-2xl border border-border/80 bg-card/95 px-4 py-3 text-left shadow-xl backdrop-blur transition-all duration-200 hover:shadow-2xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
        aria-label={summary.t('BETSLIP')}
      >
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-foreground">
              <i className="bi bi-receipt text-base" />
              <span>{summary.t('BETSLIP')}</span>
              <span className="rounded-full bg-neutral px-2 py-0.5 text-[11px] font-bold text-neutral-foreground">
                {summary.countLabel}
              </span>
            </div>
            <div className="mt-1 truncate text-xs text-muted-foreground">
              {summary.t('TOTAL_STAKE')}: <span className="font-semibold text-foreground">{summary.stake}</span>
              {' · '}
              {summary.t('POSSIBLE_PAYOUT')}: <span className="font-semibold text-primary">{summary.payout}</span>
            </div>
          </div>
          <i className="bi bi-chevron-up text-base text-muted-foreground" aria-hidden="true" />
        </div>
      </button>
    </div>
  );
}

function Betslip({ isLgUp }: { isLgUp: boolean }) {
  const [isOpen, setIsOpen] = React.useState(false);
  const setBetslipTab = useTicketSlipStore((s) => s.setBetslipTab);

  React.useEffect(() => {
    setIsOpen(false);
  }, [isLgUp]);

  const handleClose = () => {
    setBetslipTab('slip');
    setIsOpen(false);
  };

  return (
    <>
      {isLgUp ? (
        <DesktopTrigger isOpen={isOpen} onOpen={() => setIsOpen(true)} />
      ) : (
        <MobileSummaryBar isOpen={isOpen} onOpen={() => setIsOpen(true)} />
      )}
      {isLgUp ? (
        <DesktopPanel isOpen={isOpen} onClose={handleClose} />
      ) : (
        <MobilePanel isOpen={isOpen} onClose={handleClose} />
      )}
    </>
  );
}

export default Betslip;
